// ═══════════════════════════════════════════════════════
// NAVEGAÇÃO — itens do menu lateral do cliente
// Icons: SVG line-thin (1.5px stroke) — brand-aligned
// Grouped: accordion/dropdown navigation structure
// ═══════════════════════════════════════════════════════
import { NAV_ICONS } from "./icons.jsx";

// ── Type Interfaces ──────────────────────────────────

export interface NavItem {
  id: string;
  Icon: React.ComponentType;
  label: string;
  hidden?: boolean;
}

export interface NavGroup {
  id: string;
  Icon: React.ComponentType;
  label: string;
  isGroup: true;
  defaultOpen?: boolean;
  children: NavItem[];
}

export type NavEntry = NavItem | NavGroup;

export const NAV: NavEntry[] = [
  { id: "dashboard", Icon: NAV_ICONS.dashboard, label: "Dashboard" },
  { id: "patrimonio-total", Icon: NAV_ICONS["patrimonio-total"], label: "Patrimônio Total" },
  {
    id: "investimentos",
    Icon: NAV_ICONS.performance,
    label: "Investimentos",
    isGroup: true,
    defaultOpen: true,
    children: [
      { id: "carteira", Icon: NAV_ICONS.carteira, label: "Carteira" },
      { id: "performance", Icon: NAV_ICONS.performance, label: "Performance" },
      { id: "metricas", Icon: NAV_ICONS.metricas, label: "Métricas" },
      { id: "dividendos", Icon: NAV_ICONS.dividendos, label: "Dividendos" },
      { id: "alocacao", Icon: NAV_ICONS.alocacao, label: "Alocação" },
    ],
  },
  {
    id: "operacoes",
    Icon: NAV_ICONS.movimentacoes,
    label: "Operações",
    isGroup: true,
    children: [
      { id: "movimentacoes", Icon: NAV_ICONS.movimentacoes, label: "Movimentações", hidden: true },
      { id: "aportes", Icon: NAV_ICONS.aportes, label: "Aportes" },
      { id: "relatorios", Icon: NAV_ICONS.relatorios, label: "Relatórios" },
      {
        id: "schedule-delivery",
        Icon: NAV_ICONS["schedule-delivery"],
        label: "Agenda & Entregas",
        hidden: true,
      },
    ],
  },
  {
    id: "planejamento",
    Icon: NAV_ICONS.metas,
    label: "Planejamento",
    isGroup: true,
    children: [
      { id: "cenarios", Icon: NAV_ICONS.cenarios, label: "Cenários" },
      { id: "metas", Icon: NAV_ICONS.metas, label: "Metas & Mapeamento" },
      { id: "hedge", Icon: NAV_ICONS.hedge, label: "Hedge & Proteção", hidden: true },
      { id: "amortizacao", Icon: NAV_ICONS.amortizacao, label: "Amortização de Passivos" },
      { id: "aposentadoria", Icon: NAV_ICONS.aposentadoria, label: "Aposentadoria" },
      { id: "sucessao", Icon: NAV_ICONS.sucessao, label: "Sucessão Patrimonial" },
    ],
  },
  {
    id: "fiscal",
    Icon: NAV_ICONS.calcir,
    label: "Fiscal",
    isGroup: true,
    children: [
      { id: "calcir", Icon: NAV_ICONS.calcir, label: "Calculadora IR" },
      { id: "simulador-tributario", Icon: NAV_ICONS.calcir, label: "Simulador PF vs PJ" },
      { id: "informeir", Icon: NAV_ICONS.informeir, label: "Informe IR", hidden: true },
      { id: "custos", Icon: NAV_ICONS.custos, label: "Custos e Taxas", hidden: true },
    ],
  },
  {
    id: "recursos",
    Icon: NAV_ICONS.educacao,
    label: "Recursos",
    isGroup: true,
    children: [
      { id: "documentos", Icon: NAV_ICONS.documentos, label: "Documentos" },
      { id: "educacao", Icon: NAV_ICONS.educacao, label: "Educação" },
      { id: "privacidade", Icon: NAV_ICONS.documentos, label: "Privacidade", hidden: true },
    ],
  },
];

/** Perfil item — rendered separately in sidebar footer */
export const PERFIL_NAV: NavItem = { id: "perfil", Icon: NAV_ICONS.perfil, label: "Meu Perfil" };

/** Flatten all items (top-level + children) into array of page items (excl groups) */
export function flattenNav(nav: NavEntry[] = NAV): NavItem[] {
  return nav.reduce<NavItem[]>((acc, item) => {
    if ("isGroup" in item && item.isGroup) acc.push(...item.children);
    else acc.push(item as NavItem);
    return acc;
  }, []);
}

/** Find which group contains a page ID (or null for top-level) */
export function findGroupForPage(pageId: string, nav: NavEntry[] = NAV): NavGroup | null {
  return (
    (nav.find(
      (g): g is NavGroup =>
        "isGroup" in g && g.isGroup === true && g.children?.some((c) => c.id === pageId)
    ) as NavGroup | undefined) || null
  );
}
